/**
 * Die Texte der **app-eigenen** Versandkategorien `report` und `invoice-delivery` im Namensraum `emailOutbox`, zwölf
 * Sprachen (Issue API 2026-10-02 `ia-fe-ohne-postausgang-seite`; `invoice-delivery` seit IA-BE 3.17.1, KW-023).
 *
 * IA-BE meldet `report` beim Kernel an und schickt `labelKey`/`descriptionKey` mit
 * (`saas-invoice-api-backend` `src/email/dispatch-categories.ts`). Das `emailOutbox`-Bündel des Kernels führt nur
 * die Kernel-Kategorien; ohne diese Datei zeigte „E-Mail-Versand" für `report` den festen `{de,en}`-Satz aus dem
 * Backend-Katalog, in den übrigen zehn Sprachen also Englisch.
 *
 * `invoice-delivery` (ENTSCHEID observer 16039): `de`, `en` wortgleich aus IA-BE 3.17.1
 * `src/email/dispatch-categories.ts:54/:56/:57`; die übrigen zehn sind eine Übersetzung der Lane aus `en` — es gibt
 * keine Vorlage in einer anderen App. Die Begriffe folgen dem `emailOutbox`-Satz des Kernels derselben Sprache
 * (Rechnung, Empfänger, Mail, Protokoll); „API“ bleibt unübersetzt. Die Herkunft steht je Eintrag als Kommentar.
 *
 * Herkunft des `report`-Wortlauts, nicht frei übersetzt:
 * - `de`, `en`: der Satz des IA-BE-Katalogs, wortgleich (er ist zugleich der Rückfall).
 * - die übrigen zehn: der `report`-Eintrag von verein-FE (`src/locales/overrides/emailOutbox.<sprache>.ts`), ohne den
 *   Empfänger „an den Vorstand“ — IA-BE meldet denselben Satz ohne ihn. Der Abgleich ist an `en` belegt: verein
 *   „Due report subscriptions and alerts to the board.“ ohne „to the board“ ist der IA-BE-Satz.
 *
 * Wächter: `tests/locales/versandkategorien-in-zwoelf-sprachen.test.ts` (Soll aus der installierten
 * IA-BE-Typdeklaration, Ist aus `saasConfig.languages.overrides`).
 */

/** Label und Beschreibung einer app-eigenen Versandkategorie — beide Pflicht. */
type VersandkategorieText = {
  readonly label: string
  readonly description: string
}

/** Der `dispatchCategory`-Zweig einer Sprache; der Namensraum `emailOutbox:` steht im Schlüssel des Backends. */
type VersandkategorienText = {
  readonly dispatchCategory: Readonly<Record<string, VersandkategorieText>>
}

export const versandkategorienLocales: Record<string, VersandkategorienText> = {
  bg: {
    dispatchCategory: {
      report: { label: 'Отчети', description: 'Дължими абонаменти за отчети и сигнали.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Изпращане на фактури чрез API',
        description:
          'Генерирани фактури, които API изпраща до получателите на заявката. Доставените имейли не се записват в протокола.',
      },
    },
  },
  cs: {
    dispatchCategory: {
      report: { label: 'Přehledy', description: 'Splatné odběry přehledů a upozornění.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Odesílání faktur přes API',
        description:
          'Vygenerované faktury, které API odesílá příjemcům volání. Doručené e-maily se nezapisují do protokolu.',
      },
    },
  },
  de: {
    dispatchCategory: {
      report: { label: 'Berichte', description: 'Fällige Berichts-Abos und Alarme.' },
      // label: wortgleich IA-BE 3.17.1 src/email/dispatch-categories.ts:54 (de); description: ebd. :56
      'invoice-delivery': {
        label: 'Rechnungsversand über die API',
        description:
          'Erzeugte Rechnungen, die die API an die Empfänger des Aufrufs schickt. Zugestellte Mails werden nicht protokolliert.',
      },
    },
  },
  en: {
    dispatchCategory: {
      report: { label: 'Reports', description: 'Due report subscriptions and alerts.' },
      // label: wortgleich IA-BE 3.17.1 src/email/dispatch-categories.ts:54 (en); description: ebd. :57
      'invoice-delivery': {
        label: 'Invoice delivery via the API',
        description:
          'Generated invoices the API sends to the recipients of the call. Delivered mails are not logged.',
      },
    },
  },
  es: {
    dispatchCategory: {
      report: { label: 'Informes', description: 'Suscripciones a informes vencidas y alertas.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Envío de facturas a través de la API',
        description:
          'Facturas generadas que la API envía a los destinatarios de la llamada. Los correos entregados no se guardan en el registro.',
      },
    },
  },
  fr: {
    dispatchCategory: {
      report: {
        label: 'Rapports',
        description: 'Abonnements aux rapports arrivés à échéance et alertes.',
      },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Envoi de factures via l’API',
        description:
          'Factures générées que l’API envoie aux destinataires de l’appel. Les e-mails remis ne sont pas consignés dans le journal.',
      },
    },
  },
  hu: {
    dispatchCategory: {
      report: { label: 'Jelentések', description: 'Esedékes jelentés-előfizetések és riasztások.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Számlaküldés az API-n keresztül',
        description:
          'Létrehozott számlák, amelyeket az API a hívás címzettjeinek küld. A kézbesített e-mailek nem kerülnek a naplóba.',
      },
    },
  },
  it: {
    dispatchCategory: {
      report: { label: 'Report', description: 'Abbonamenti ai report in scadenza e avvisi.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Invio di fatture tramite l’API',
        description:
          'Fatture generate che l’API invia ai destinatari della chiamata. Le e-mail consegnate non vengono salvate nel registro.',
      },
    },
  },
  nl: {
    dispatchCategory: {
      report: { label: 'Rapporten', description: 'Verlopen rapportabonnementen en meldingen.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Factuurverzending via de API',
        description:
          'Gegenereerde facturen die de API naar de ontvangers van de aanroep stuurt. Bezorgde e-mails worden niet in het logboek opgenomen.',
      },
    },
  },
  pl: {
    dispatchCategory: {
      report: { label: 'Raporty', description: 'Należne subskrypcje raportów i alerty.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Wysyłka faktur przez API',
        description:
          'Wygenerowane faktury, które API wysyła do odbiorców wywołania. Dostarczone e-maile nie są zapisywane w dzienniku.',
      },
    },
  },
  pt: {
    dispatchCategory: {
      report: { label: 'Relatórios', description: 'Subscrições de relatórios vencidas e alertas.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Envio de faturas através da API',
        description:
          'Faturas geradas que a API envia aos destinatários da chamada. Os e-mails entregues não são guardados no registo.',
      },
    },
  },
  ro: {
    dispatchCategory: {
      report: { label: 'Rapoarte', description: 'Abonamente la rapoarte scadente și alerte.' },
      // label, description: Übersetzung Lane aus en (BE 3.17.1)
      'invoice-delivery': {
        label: 'Trimiterea facturilor prin API',
        description:
          'Facturi generate pe care API-ul le trimite destinatarilor apelului. E-mailurile livrate nu sunt înregistrate în jurnal.',
      },
    },
  },
}
